import create from 'zustand'
import { fetchRequest } from './'
import { Api, subscription, Subscription } from './api'
import { LocationDepartmentAccount } from './domain'
import { formApi, FormApi } from './form'
import { showToast } from './toast'
import { worksheetMessage, WorksheetResult } from './worksheet-service'

export type Feature<T = any> = {
  feature: string
  enabled?: boolean
  create?: boolean
  update?: boolean
  delete?: boolean
  audit?: boolean
  parameters?: T
  activity?: string
}

export type Activity = {
  name: string
  features: Feature[]
}

interface Features {
  [key: string]: Feature[]
}

export type Workflow = {
  name: string
  prompt: string
  featuresByActivity: Features
  activities?: Activity[]
}

const service = 'codingconfig'

type KnownWorkflows = {
  codingStd: Workflow
  auditStd: Workflow
  codeReviewStd: Workflow
}

export type KnownWorkflow = keyof KnownWorkflows

type KnownActivities = {
  AUDITING: Activity
  CODING: Activity
  REBUTTAL: Activity
  REBUTTALRESPONSE: Activity
}
type KnownActivity = keyof KnownActivities | string

type IdParam = {
  id: string
}

type MinMax = {
  min: number
  max: number
}

export type WorksheetTypesParam = {
  worksheetTypes: string[]
}

type KnownFeatures = {
  WorksheetTrackingCodes: Feature
  VisitTrackingCodes: Feature
  TruCode: Feature
  ProviderComms: Feature
  MacraMIPS: Feature
  LostRevenue: Feature
  EMCalculator: Feature
  DxAndProcedures: Feature
  DownCodes: Feature
  CoderNotes: Feature
  CCIEdits: Feature
  NCNBReasons: Feature
  DocumentProcessorVersion: Feature<IdParam>
  ReasonForVisit: Feature<MinMax>
  PresentOnAdmission: Feature<WorksheetTypesParam>
  secondaryProvider: Feature
  tertiaryProvider: Feature
}

export type KnownFeature = keyof KnownFeatures

type WorkflowQuery = LocationDepartmentAccount & {
  workflow?: KnownWorkflow | string
  activity?: KnownActivity
  feature?: KnownFeature
  preloadDepartments?: string[]
}

type WorkflowResponse = WorksheetResult<Workflow[]>
type WorflowResponseCache = {
  [key: string]: WorkflowResponse
}
const cache: WorflowResponseCache = {}
function cacheWorflowResponse(args: WorkflowQuery, response?: WorkflowResponse) {
  if (args?.location) {
    const key = args.location + args.department
    if (!response) {
      return cache[key]
    } else {
      cache[key] = response
    }
  }
}
export const getWorkflowConfigRequest = fetchRequest<LocationDepartmentAccount, WorkflowResponse>({
  method: 'post',
  cacheResult: (args) => cacheWorflowResponse(args),
  onResult: cacheWorflowResponse,
  body: (args) =>
    worksheetMessage(
      {
        locationCode: args.location,
        departmentCode: args.department,
      },
      'getlocationdepartmentconfig',
      service,
      currentWorkflow(),
    ),
})

function augmentWorkflow(raw: Workflow): Workflow {
  const activities: Activity[] = []
  Object.entries(raw.featuresByActivity).forEach(([name, features]) => {
    activities.push({ name, features: prepareFeatures(features) })
  })
  return { ...raw, ...{ activities } }
}

function prepareFeatures(features: Feature[]): Feature[] {
  return features?.map((f) => ({
    ...f,
    parameters: parseFeatureParameters(f.parameters),
  }))
}

/**
 * Trying to parse the feature params if it is a valid JSON.
 * Falling back to original value if not.
 */
function parseFeatureParameters(params: string): string | object | undefined {
  if (!params) {
    return params
  }

  try {
    return JSON.parse(params)
  } catch (e) {
    return params
  }
}

type WorkflowApiConfiguration = {
  workflowSelector?: boolean
  featureForm?: boolean
}

type WorkflowData = {
  query: WorkflowQuery
  initialWorkflow: string
  workflows: Workflow[]
  workflow: Workflow
  activity: Activity
  feature: Feature
  showActivities: boolean
  workflowSelectorOpen: boolean
  workflowChanged?: string
}

type WorkflowActions = {
  load(organization?: WorkflowQuery): Promise<WorkflowData>
  workflowSelectorOpen(open: boolean, showActivities?: boolean): void
  featureForm(feature: Feature): FormApi<FeatureForm>
  featureForms(activity: Activity): [Feature, FormApi<FeatureForm>][]
  find(query: WorkflowQuery): WorkflowData
  setWorkflow(a: Workflow | string): void
  allWorkflows(): Workflow[]
}

type WorkflowForm = { workflow: Workflow }
type ActivityForm = { activity: Activity }
type FeatureForm = Partial<Feature>

type WorflowForms = {
  workflow: FormApi<WorkflowForm>
  activity: FormApi<ActivityForm>
  features: FormApi<FeatureForm>[]
}

type WorkflowEvents = Subscription<WorkflowData>

export type WorkflowApi = Api<WorkflowApiConfiguration, WorkflowData, WorkflowActions, WorflowForms, WorkflowEvents>

function workflowApi(config?: WorkflowApiConfiguration): WorkflowApi {
  const configuration = {
    ...{ workflowSelector: true, featureForm: true },
    ...config,
  }

  const data = create<WorkflowData>(() => ({
    query: null,
    initialWorkflow: null,
    workflows: null,
    workflow: null,
    activity: null,
    feature: null,
    showActivities: false,
    workflowSelectorOpen: false,
    workflowChanged: null,
  }))

  const activity = formApi<ActivityForm>({
    values: {
      activity: null,
    },
    fields: {
      activity: {
        label: 'Activity',
        list: data.getState().workflows || [],
        listLabel: (v: Activity) => v.name,
        onChange: (v: Activity) => {
          // console.log('Activity change', v)
          if (v) {
            if (configuration.featureForm && v.features) {
              forms.features = v.features.map((f) => featureForm(f))
            }
            data.setState({ activity: v })
          }
        },
      },
    },
  })

  function getWorkflow(name: string): Workflow {
    const list = data.getState().workflows
    if (list) {
      return list.filter((a) => a.name === name)[0]
    }
    return null
  }

  function allWorkflows(): Workflow[] {
    const list = data.getState().workflows
    return list || []
  }
  function setWorkflow(a: Workflow | string) {
    const v = typeof a === 'string' ? getWorkflow(a) : a
    if (v) {
      const update: Partial<WorkflowData> = { workflow: v }
      update.workflowChanged = v.name

      data.setState(update as any)

      if (v.activities?.length) {
        activity.controls.activity.list.setState({ value: v.activities })
      }
    }
  }

  const workflow = formApi<WorkflowForm>({
    values: {
      workflow: null,
    },
    fields: {
      workflow: {
        label: 'Workflow',
        list: [],
        listLabel: (v: Workflow) => v.prompt,
        onChange: (v: Workflow) => {
          setWorkflow(v)
        },
      },
    },
  })

  const featureForm = (f: Feature): FormApi<FeatureForm> => {
    const onChange = (values: FeatureForm) => {
      f.enabled = values.enabled
      f.create = values.create
      f.update = values.update
      f.delete = values.delete
      f.audit = values.audit
      data.setState({ feature: { ...f } })
    }
    const form = formApi<FeatureForm>({
      onChange,
      values: {
        enabled: f.enabled,
        create: f.create,
        update: f.update,
        delete: f.delete,
        audit: f.audit,
      },
      fields: {
        enabled: {
          label: 'Enabled',
          selected: f.enabled,
        },
        create: {
          label: 'Create',
          selected: f.create,
        },
        update: {
          label: 'Update',
          selected: f.update,
        },
        delete: {
          label: 'Delete',
          selected: f.delete,
        },
        audit: {
          label: 'Audit',
          selected: f.audit,
        },
      },
    })
    return form
  }
  const featureForms: any = (v: Activity) => v.features.map((f) => [f, featureForm(f)])

  function find(query: WorkflowQuery): WorkflowData {
    const { workflow, activity, feature } = query

    const w: Workflow = workflow
      ? data.getState().workflows.filter((v) => v.name === workflow)[0]
      : data.getState().workflow

    const a: Activity =
      w && activity
        ? w.activities.filter((v) => v.name === activity)[0]
        : w
          ? w.activities[0]
          : data.getState().activity

    const f: Feature = a ? a.features.filter((v) => v.feature === feature)[0] : data.getState().feature

    return { ...data.getState(), ...{ workflow: w, activity: a, feature: f } }
  }

  const forms: WorflowForms = {
    workflow,
    activity,
    features: [],
  }

  const events: WorkflowEvents = subscription<WorkflowData>(
    ['query', 'workflows', 'workflow', 'activity', 'feature', 'workflowChanged'],
    data,
  )

  const actions: WorkflowActions = {
    featureForm,
    featureForms,
    find,
    setWorkflow,
    allWorkflows,
    load: async (args?: WorkflowQuery) => {
      const loc = {
        ...{ activity: 'CODING' },
        ...data.getState().query,
        ...args,
      }
      if (!loc || !loc.location || !loc.department) {
        throw new Error('Workflow api requires a valid organization select to load configuration.')
      }

      const response: WorkflowResponse = await getWorkflowConfigRequest(args)
      // console.log('workflow', args, response)

      if (!response?.success || !response?.result) {
        const message = response?.errors
          ? response?.errors.join(',')
          : `Could not load a valid workflow configuration for ${loc.location} ${loc.department}`
        showToast(message)
        return Promise.reject(message)
      }

      const workflows = response.result.map((v) => augmentWorkflow(v))
      if (!workflows.length) {
        const message = `No worflow configurations were returned for ${loc.location} ${loc.department}`
        showToast(message)
        return Promise.reject(message)
      }

      workflow.controls.workflow.list.setState({ value: workflows })

      data.setState({ workflows, query: loc })

      if (args.workflow || args.activity) {
        let queryResult: WorkflowData = find({
          workflow: args.workflow,
          activity: args.activity,
        })
        queryResult.workflowChanged = queryResult?.workflow?.name
        data.setState(queryResult)
      }

      if (args.preloadDepartments?.length) {
        args.preloadDepartments.forEach((department) => {
          getWorkflowConfigRequest({ ...args, ...{ department } })
        })
      }
      return Promise.resolve(data.getState())
    },
    workflowSelectorOpen: (workflowSelectorOpen: boolean, showActivities = false) => {
      data.setState({ showActivities })

      workflow.controls.workflow.list.setState({
        value: data.getState().workflows || [],
      })

      const w = data.getState().workflow
      if (w) {
        workflow.controls.workflow.setListValue((a: Workflow) => {
          return a?.prompt === w?.prompt
        })
      }

      data.setState({ workflowSelectorOpen })
    },
  }

  return {
    configuration,
    data,
    actions,
    forms,
    events,
  }
}

export const workflows = workflowApi()

export function featureEnabled(feature: KnownFeature, defaultEnabled = true): boolean {
  const w = workflows.data.getState().workflow
  if (!w) {
    return defaultEnabled
  }
  const f = workflows.actions.find({ feature }).feature
  return f ? f.enabled : false
}

export function findFeature(feature: KnownFeature, query?: WorkflowQuery): Feature {
  const w = workflows.data.getState().workflow

  if (query) {
    //TODO: support multiple department workflows via API
    const { location, department } = query
    if (location && department) {
      const key = location + department
      const list: Workflow[] = cache[key]?.result

      if (list?.length) {
        const name = query.workflow || w?.name
        const activity = query.activity || 'CODING'
        const v = list.filter((a) => a.name === name)[0]

        // Deep links (ie. Data Admin) open a worksheet without its workflow and the app
        // defaults the name, so a workflow that does not declare the feature means
        // unknown, not disabled. Fall back to the other workflows configured for this
        // location/department. A workflow that does declare it still wins, disabled or not.
        const candidates = v ? [v, ...list.filter((a) => a !== v)] : list

        for (const candidate of candidates) {
          const features = candidate.featuresByActivity[activity]
          const f = features?.filter((a) => a.feature === feature)[0]
          if (f) {
            return f
          }
        }
        return null
      }
      return null
    }
  } else {
    if (!w) {
      return null
    }
    return workflows.actions.find({ feature }).feature
  }
}

export function currentWorkflow(): string {
  const w = workflows.data.getState().workflow
  if (!w) {
    return undefined
  }
  return w.name
}

export function setCurrentWorkflow(): string {
  const w = workflows.data.getState().workflow
  if (!w) {
    return undefined
  }
  return w.name
}

export function currentWorkflowPrompt(defaultPrompt = 'Coding'): string {
  const w = workflows.data.getState().workflow
  if (!w) {
    return defaultPrompt
  }
  return w.prompt
}

export function isReviewWorkflow(): boolean {
  const workflow = workflows.data((s) => s.workflow)
  const name = (workflow?.name || '').toLowerCase()

  return (
    (name.indexOf('audit') > -1 ||
    name.indexOf('review') > -1 ||
    name.includes('audit') ||
    name.includes('review') ||
    name.includes('rebutt'))
  )
}

export function isAuditReviewWorkflow(): boolean {
  const workflow = workflows.data((s) => s.workflow)
  const name = (workflow?.name || '').toLowerCase()

  return (
    (name.indexOf('audit') > -1 ||
    name.indexOf('review') > -1 ||
    name.includes('audit') ||
    name.includes('review') ||
    name.includes('rebutt')) && 
    !name.includes('AutonomousCodeReview_Default'.toLowerCase())
  )
}

export function isAuditWorkflow(): boolean {
  const workflow = workflows.data((s) => s.workflow)

  return workflow?.featuresByActivity && Object.keys(workflow?.featuresByActivity)[0]?.toLowerCase() === 'audit'
}

let defaultActivity = 'CODING'
export function setDefaultActivity(activity: string) {
  if (activity) {
    defaultActivity = activity
  }
}
export function getActivity(): string {
  const workflow = workflows.data.getState().workflow
  if (!workflow) {
    return defaultActivity
  }
  const activity = workflow.activities[0]
  return activity?.name || defaultActivity
}

const defaultWorkflow = 'auditStd'
export function getWorkflow(): string {
  const workflow = workflows.data.getState().workflow
  if (!workflow) {
    return workflows.data.getState().initialWorkflow || defaultWorkflow
  }
  return workflow?.name || defaultActivity
}

export function isReviewWorkflowState(): boolean {
  const activity = getActivity()
  if (activity === 'REVIEW' || activity === 'AUDIT') {
    return true
  }
  const workflow = workflows.data.getState().workflow
  const name = (workflow?.name || '').toLowerCase()
  return (name.indexOf('audit') > -1 || name.indexOf('review') > -1 )
}

export function isReviewWorkflowTransitionState(): boolean {
  const activity = getActivity()
  if (activity === 'REVIEW' || activity === 'AUDIT') {
    return true
  }
  const workflow = workflows.data.getState().workflow
  const name = (workflow?.name || '').toLowerCase()
  return (name.indexOf('audit') > -1 || name.indexOf('review') > -1 ) && 
  !name.includes('AutonomousCodeReview_Default'.toLowerCase())
}

export function getDocumentProcessorVersion(): '1' | '2' {
  const f: Feature<IdParam> = findFeature('DocumentProcessorVersion')
  if (!f) {
    return '1'
  }
  return f.parameters?.id === '2' ? '2' : '1'
}

export function isReasonForVisitEnabled(department: string, location: string): boolean {
  const data = workflows.data.getState()
  const activity = data.activity?.name
  return findFeature('ReasonForVisit', {
    department,
    location,
    activity,
  })?.enabled
}

export const isInlineEditEnabled = (activity: string): boolean => {
  return activity === 'CODING'
}

export function isMultiSendbackEnabled(_department: string, location: string): boolean {
  if (!location) {
    return false
  }
  /* TODO
  const data = workflows.data.getState();
  const activity = data.activity?.name;
  return findFeature('MultiSendback', {
    department,
    location,
    activity,
  })?.enabled;
  */
  return location.includes('USACS') || location.includes('CAPH_')
}

export function isAlertsV2(location: string): boolean {
  return true
}
